import * as React from 'react'
import styled from 'styled-typed'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { State } from 'store'
import { User } from 'auth/state'
import { PageTitle, Link } from 'uiComponents/typography'
import { TextInput } from 'uiComponents/input/textInput'
import {
    Container,
    ContainerHeading,
    ContainerBody,
    HeadingSectionName,
    Section,
    SectionForm,
    InputRow,
    InputColumn,
    PageActions,
} from 'uiComponents/settingsContainer'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { isValidUrl } from 'utils/formFieldChecks'
import { startOfToday } from 'date-fns'
import { ActionButton } from 'uiComponents/buttons'
import { AccountServiceContext, ProductsListServiceContext, ArticleServiceContext } from 'admin/context'
import { PricingServiceContext } from 'products/pricing/context'
import { ComponentsServiceContext } from 'engageTools/studio/context'
import { UserServiceContext } from 'auth/context'
import { createAccount } from './onboarding'
import { defaultOnboardingTemplateFor } from './onboardingTemplate'
import { PageLoader } from 'uiComponents/loaders'
import { CopySnippetElement } from 'snippet/copySnippetElement'
import { userDataRetrieved } from 'auth/actions'
import { SingleSelect } from 'uiComponents/input'
import { MarketType } from 'settings/accountSettings/accountInfo'
import { activeAccountChanged } from 'preferences/actions'
import { hideAllMessages, showErrorMessage } from 'uiComponents/message'
import { useTranslation } from 'translations'

interface OnboardingPageProps {
    user: User | null
    userDataRetrieved: typeof userDataRetrieved
    activeAccountChanged: typeof activeAccountChanged
    checkoutDemoEndpoint: string
}

function slugify(str: string) {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/,:;'
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz_____'
    const p = new RegExp(a.split('').join('|'), 'g')

    return str
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(p, (c) => b.charAt(a.indexOf(c)))
        .replace(/&/g, '_and_')
        .replace(/[^\w\-]+/g, '')
        .replace(/__+/g, '_')
        .replace(/^_+/, '')
        .replace(/_+$/, '')
}

function isEmail(email: string) {
    var re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
}

const DemoLink = styled(Link)`
    display: block;
    margin-bottom: 1em;
`

function OnboardingPageComponent(props: OnboardingPageProps) {
    const { t } = useTranslation()
    const accountService = React.useContext(AccountServiceContext)
    const productsListService = React.useContext(ProductsListServiceContext)
    const articleService = React.useContext(ArticleServiceContext)
    const pricingService = React.useContext(PricingServiceContext)
    const componentsService = React.useContext(ComponentsServiceContext)
    const userService = React.useContext(UserServiceContext)
    const [accountName, setAccountName] = React.useState('')
    const [accountNameError, setAccountNameError] = React.useState<string | null>(null)
    const [websiteUrl, setWebsiteUrl] = React.useState('')
    const [websiteUrlError, setWebsiteUrlError] = React.useState<string | null>(null)
    const [adyenAccountName, setAdyenAccountName] = React.useState('')
    const [adyenAccountNameError, setAdyenAccountNameError] = React.useState<string | null>(null)
    const [adyenCountryCode, setAdyenCountryCode] = React.useState('')
    const [adyenCountryCodeError, setAdyenCountryCodeError] = React.useState<string | null>(null)
    const [supportAgentEmail, setSupportAgentEmail] = React.useState('')
    const [supportAgentEmailError, setSupportAgentEmailError] = React.useState<string | null>(null)
    const [marketType, setMarketType] = React.useState<MarketType>('Europe')
    const [acountCreating, setAccountCreating] = React.useState(false)
    const [createdAccountSlug, setCreatedAccountSlug] = React.useState<string | null>(null)

    const fullWebsiteUrl = /^https?:\/\//i.test(websiteUrl) ? websiteUrl : 'https://' + websiteUrl

    function reset() {
        setAccountName('')
        setWebsiteUrl('')
        setAdyenAccountName('')
        setAdyenCountryCode('')
        setSupportAgentEmail('')
        setMarketType('Europe')
        setAccountNameError(null)
        setWebsiteUrlError(null)
        setAdyenAccountNameError(null)
        setAdyenCountryCodeError(null)
        setSupportAgentEmailError(null)
        setCreatedAccountSlug(null)
    }

    function validate() {
        setAccountNameError(null)
        setWebsiteUrlError(null)
        setAdyenAccountNameError(null)
        setAdyenCountryCodeError(null)
        setSupportAgentEmailError(null)

        let result = true
        if (!accountName) {
            setAccountNameError(t('admin.onboarding.requiredField'))
            result = false
        }
        if (!websiteUrl) {
            setWebsiteUrlError(t('admin.onboarding.requiredField'))
            result = false
        } else if (!isValidUrl(fullWebsiteUrl)) {
            setWebsiteUrlError(t('admin.onboarding.invalidUrl'))
            result = false
        }
        if (!adyenAccountName) {
            setAdyenAccountNameError(t('admin.onboarding.requiredField'))
            result = false
        }
        if (!adyenCountryCode) {
            setAdyenCountryCodeError(t('admin.onboarding.requiredField'))
            result = false
        }
        if (!supportAgentEmail) {
            setSupportAgentEmailError(t('admin.onboarding.requiredField'))
            result = false
        } else if (!isEmail(supportAgentEmail)) {
            setSupportAgentEmailError(t('admin.onboarding.invalidEmail'))
            result = false
        }

        return result
    }

    const onSave = async (evt: React.FormEvent<HTMLElement>) => {
        evt.preventDefault()
        hideAllMessages()
        if (!validate()) {
            return
        }

        const today = startOfToday()
        const endOfNextYear = new Date(today.getFullYear() + 1, 11, 31)
        const accountSlug = slugify(accountName)
        const onboardingTemplate = defaultOnboardingTemplateFor(
            accountSlug,
            accountName,
            fullWebsiteUrl,
            adyenAccountName,
            adyenCountryCode,
            supportAgentEmail,
            marketType,
            today,
            endOfNextYear,
        )

        try {
            setAccountCreating(true)
            const accountInfo = await accountService.getAccount(accountSlug)
            if (accountInfo) {
                showErrorMessage(t('admin.onboarding.slugExists', { accountSlug }))
                return
            }

            await createAccount(
                accountService,
                productsListService,
                articleService,
                pricingService,
                componentsService,
                onboardingTemplate,
            )

            const userDetails = await userService.getLoggedInUser()
            props.userDataRetrieved(userDetails.currentUser)
            setCreatedAccountSlug(accountSlug)
            props.activeAccountChanged(accountSlug)
        } catch {
            showErrorMessage(t('admin.onboarding.unknownError'))
        } finally {
            setAccountCreating(false)
        }
    }

    const createdAccount =
        createdAccountSlug && props.user ? props.user.accounts.find((a) => a.slug === createdAccountSlug) : null
    return (
        <>
            <PageTitle>{t('admin.onboarding.pageTitle')}</PageTitle>
            {acountCreating && <PageLoader />}
            {!acountCreating && (
                <>
                    {!createdAccount && (
                        <Container>
                            <ContainerHeading>
                                <HeadingSectionName className="active" to="" disabled>
                                    {t('admin.onboarding.accountSection')}
                                </HeadingSectionName>
                            </ContainerHeading>
                            <ContainerBody>
                                <Section>
                                    <SectionForm noValidate onSubmit={onSave}>
                                        <div>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="accountName">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.partnerName')}
                                                        </FormItemName>
                                                        <TextInput
                                                            id="accountName"
                                                            name="accountName"
                                                            type="text"
                                                            value={accountName}
                                                            onChange={(e) => setAccountName(e.target.value)}
                                                            block
                                                        />
                                                    </FormItem>
                                                    <ValidationMessage
                                                        className={accountNameError ? 'validation-message-visible' : ''}
                                                    >
                                                        {accountNameError || ''}
                                                    </ValidationMessage>
                                                </InputColumn>
                                            </InputRow>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="accountSlug">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.accountSlug')}
                                                        </FormItemName>
                                                        <TextInput
                                                            id="accountSlug"
                                                            name="accountSlug"
                                                            type="text"
                                                            value={slugify(accountName)}
                                                            block
                                                            disabled
                                                        />
                                                        <ValidationMessage />
                                                    </FormItem>
                                                </InputColumn>
                                            </InputRow>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="websiteUrl">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.websiteUrl')}
                                                        </FormItemName>
                                                        <TextInput
                                                            id="websiteUrl"
                                                            name="websiteUrl"
                                                            type="text"
                                                            value={websiteUrl}
                                                            onChange={(e) => setWebsiteUrl(e.target.value)}
                                                            block
                                                        />
                                                    </FormItem>
                                                    <ValidationMessage
                                                        className={websiteUrlError ? 'validation-message-visible' : ''}
                                                    >
                                                        {websiteUrlError || ''}
                                                    </ValidationMessage>
                                                </InputColumn>
                                            </InputRow>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="adyenAccountName">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.adyenAccountName')}
                                                        </FormItemName>
                                                        <TextInput
                                                            id="adyenAccountName"
                                                            name="adyenAccountName"
                                                            type="text"
                                                            value={adyenAccountName}
                                                            onChange={(e) => setAdyenAccountName(e.target.value)}
                                                            block
                                                        />
                                                    </FormItem>
                                                    <ValidationMessage
                                                        className={
                                                            adyenAccountNameError ? 'validation-message-visible' : ''
                                                        }
                                                    >
                                                        {adyenAccountNameError || ''}
                                                    </ValidationMessage>
                                                </InputColumn>
                                            </InputRow>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="adyenCountryCode">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.adyenCountryCode')}
                                                        </FormItemName>
                                                        <TextInput
                                                            id="adyenCountryCode"
                                                            name="adyenCountryCode"
                                                            type="text"
                                                            value={adyenCountryCode}
                                                            onChange={(e) => setAdyenCountryCode(e.target.value)}
                                                            block
                                                        />
                                                    </FormItem>
                                                    <ValidationMessage
                                                        className={
                                                            adyenCountryCodeError ? 'validation-message-visible' : ''
                                                        }
                                                    >
                                                        {adyenCountryCodeError || ''}
                                                    </ValidationMessage>
                                                </InputColumn>
                                            </InputRow>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="supportAgentEmail">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.supportAgentEmail')}
                                                        </FormItemName>
                                                        <TextInput
                                                            id="supportAgentEmail"
                                                            name="supportAgentEmail"
                                                            type="text"
                                                            value={supportAgentEmail}
                                                            onChange={(e) => setSupportAgentEmail(e.target.value)}
                                                            block
                                                        />
                                                    </FormItem>
                                                    <ValidationMessage
                                                        className={
                                                            supportAgentEmailError ? 'validation-message-visible' : ''
                                                        }
                                                    >
                                                        {supportAgentEmailError || ''}
                                                    </ValidationMessage>
                                                </InputColumn>
                                            </InputRow>
                                            <InputRow>
                                                <InputColumn>
                                                    <FormItem htmlFor="marketType">
                                                        <FormItemName>
                                                            {t('admin.onboarding.fields.marketType')}
                                                        </FormItemName>
                                                        <SingleSelect
                                                            id="marketType"
                                                            name="marketType"
                                                            options={[
                                                                {
                                                                    value: 'Europe',
                                                                    name: t('admin.onboarding.marketTypes.europe'),
                                                                },
                                                                {
                                                                    value: 'USA',
                                                                    name: t('admin.onboarding.marketTypes.usa'),
                                                                },
                                                            ]}
                                                            selected={marketType}
                                                            onSelect={(value: MarketType) => setMarketType(value)}
                                                        />
                                                    </FormItem>
                                                </InputColumn>
                                            </InputRow>
                                        </div>
                                        <PageActions>
                                            <ActionButton type="submit">{t('common.create')}</ActionButton>
                                        </PageActions>
                                    </SectionForm>
                                </Section>
                            </ContainerBody>
                        </Container>
                    )}
                    {createdAccount && (
                        <>
                            <CopySnippetElement account={createdAccount} />
                            <p>{t('admin.onboarding.checkoutLink')}</p>
                            <br />
                            <DemoLink
                                target="__blank"
                                href={`${props.checkoutDemoEndpoint}/index.html?q=${createdAccount.slug}`}
                            >
                                {t('admin.onboarding.demo')}
                            </DemoLink>
                            <br />
                            <ActionButton onClick={reset}>{t('admin.onboarding.createAnother')}</ActionButton>
                        </>
                    )}
                </>
            )}
        </>
    )
}

export const OnboardingPage = connect(
    (state: State) => ({ user: state.auth.user }),
    (dispatch) =>
        bindActionCreators(
            {
                userDataRetrieved,
                activeAccountChanged,
            },
            dispatch,
        ),
)(OnboardingPageComponent)
